import { Link } from "react-router-dom";
import { MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import ServiceAreaMap from "@/components/ServiceAreaMap";
import {
  PHONE,
  PHONE_LINK,
  CITY_PAGES,
  WEST_VALLEY_CITIES,
  BUSINESS_NAME,
  SOCIAL_PROFILES,
} from "@/lib/constants";

const ServiceAreas = () => {
  const baseUrl = "https://truamericanplumbing.com";

  const allCities = [
    "Tolleson", "Avondale", "Goodyear", "Glendale", "Peoria", "Surprise",
    "Phoenix", "Buckeye", "Litchfield Park", "Sun City", "El Mirage",
  ];

  const areaSchema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: baseUrl },
        { "@type": "ListItem", position: 2, name: "Service Areas", item: `${baseUrl}/service-areas` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Plumbing",
      provider: {
        "@type": "Plumber",
        name: BUSINESS_NAME,
        "@id": `${baseUrl}/#organization`,
        sameAs: SOCIAL_PROFILES,
      },
      areaServed: allCities.map((city) => ({
        "@type": "City",
        name: city,
        addressRegion: "AZ",
      })),
    },
  ];

  return (
    <>
      <SEO
        title="Service Areas - Plumber in West Valley & West Phoenix"
        description="Tru American Plumbing Purifications serves Tolleson, Avondale, Goodyear, Glendale, Peoria, Surprise, Buckeye, and West Phoenix. Call (480) 322-2567 for service."
        canonicalUrl="/service-areas/"
        schema={areaSchema}
      />

      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-muted/50 to-background">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <MapPin className="h-4 w-4" />
              Serving the West Valley & West Phoenix
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Our <span className="text-gradient-copper">Service Areas</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-4">
              Based in Tolleson, Arizona, we proudly provide expert plumbing and
              water purification services throughout the West Valley and West Phoenix.
            </p>
            <p className="text-base text-foreground/70 mb-8 max-w-2xl mx-auto leading-relaxed">
              Our coverage extends roughly 20 miles from our Tolleson headquarters,
              reaching established communities like Glendale and Peoria as well as
              rapidly growing cities such as Buckeye and Goodyear. Whether your home
              was built in the 1970s or is part of a brand-new subdivision, we
              understand the plumbing systems common to each area.
            </p>
            <a href={PHONE_LINK}>
              <Button variant="hero" size="xl" className="gap-2">
                <Phone className="h-5 w-5" />
                Call {PHONE}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Interactive Map */}
      <section className="py-16 md:py-24">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-4">
            Interactive <span className="text-gradient-copper">Coverage Map</span>
          </h2>
          <p className="text-center text-muted-foreground mb-8 max-w-2xl mx-auto">
            Explore our service area across the West Valley and West Phoenix.
            Click any city marker for details.
          </p>
          <div className="max-w-5xl mx-auto">
            <ServiceAreaMap />
          </div>
        </div>
      </section>

      {/* City Pages */}
      <section className="py-16 md:py-24">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-4">
            <span className="text-gradient-copper">West Valley</span> Communities
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            The West Valley presents unique plumbing challenges. Older homes in
            Glendale and Sun City often have galvanized or polybutylene pipes that
            need replacing, while newer developments in Goodyear and Buckeye require
            proper water softening to protect modern fixtures from Arizona's
            exceptionally hard municipal water supply.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {CITY_PAGES.map((city) => (
              <Link
                key={city.slug}
                to={`/service-areas/${city.slug}/`}
                className="group p-6 bg-card border border-border rounded-xl hover:border-primary/50 transition-all text-center"
              >
                <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
                <h3 className="font-bold group-hover:text-primary transition-colors">
                  {city.name}, {city.state}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* All Cities List */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container">
          <div className="max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold mb-6 text-center">All Service Areas</h3>
            <ul className="grid grid-cols-2 gap-2">
              {WEST_VALLEY_CITIES.map((city) => (
                <li key={city} className="flex items-center gap-2 text-foreground/80">
                  <MapPin className="h-4 w-4 text-primary" />
                  {city}, AZ
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container text-center">
          <h2 className="text-3xl font-bold mb-4">
            Don't See Your City Listed?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            We may still be able to serve you! Give us a call to confirm service
            availability in your area.
          </p>
          <a href={PHONE_LINK}>
            <Button variant="hero" size="lg" className="gap-2">
              <Phone className="h-4 w-4" />
              Call {PHONE}
            </Button>
          </a>
        </div>
      </section>
    </>
  );
};

export default ServiceAreas;
